/**
 * What counts as an email address, for every form that stores one.
 *
 * The old rule lived in three copies of `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`, and it
 * had a hole: `[^\s@]+` allows a bracket, so "adame@maralawfirm.com)" saved
 * cleanly on case 4204. Loops then refused the whole email — the global one and
 * its retry — and the case manager received nothing for a mediation in
 * February. On case 4932 a participant's NAME was typed into the email field,
 * which the old rule did catch at the form but which reached the database by
 * another path, and stopped every email to one side of the case.
 *
 * Two jobs, kept apart on purpose:
 *   normalizeEmail  repairs what is unambiguously repairable — surrounding
 *                   space, angle brackets, a trailing bracket or comma.
 *   isValidEmail    judges the result and never repairs.
 *
 * These rules are a copy of scale-backend's emailAddress.helper.js and of the
 * mediator portal's utils/emailAddress.js. All three must agree: if a form
 * accepts something the sender refuses, we are back to an address that looks
 * saved and silently kills the email. The Case Manager portal had no address
 * validation at all before this, which is the other half of how case 4932 and
 * case 4204 came to hold unusable addresses.
 */

/** "Name <a@b.com>" or a pasted address with punctuation clinging to it. */
export function normalizeEmail(raw) {
    if (raw === null || raw === undefined) return ''
    let value = String(raw).trim()
    if (!value) return ''

    const angled = value.match(/<([^<>]+)>\s*$/)
    if (angled) value = angled[1].trim()

    value = value.replace(/^[\s<("'[]+/, '').replace(/[\s>)"'\].,;:]+$/, '')
    return value.trim()
}

/**
 * Narrower than the RFC on purpose: no whitespace anywhere, exactly one @, a
 * non-empty local part, and a domain with a dot and a final label of two
 * letters or more. Every address our mediators actually use passes.
 */
export function isValidEmail(value) {
    if (value === null || value === undefined) return false
    const email = String(value)
    if (!email || /\s/.test(email)) return false
    if ((email.match(/@/g) || []).length !== 1) return false
    if (email.length > 254) return false

    const [local, domain] = email.split('@')
    if (!local || local.length > 64) return false
    if (!domain || domain.length < 4) return false
    if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
        return false
    }
    return /^[^\s@,;:<>()[\]\\"]+@[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/.test(
        email
    )
}

/** Normalize, then judge. Returns the clean address, or '' if unusable. */
export function cleanEmail(raw) {
    const value = normalizeEmail(raw)
    return isValidEmail(value) ? value : ''
}

/**
 * What to show someone who typed something we cannot store. Names the problem
 * rather than saying "invalid", because "heather davis" and a stray bracket
 * need different corrections.
 */
export function emailError(raw) {
    const value = String(raw === null || raw === undefined ? '' : raw).trim()
    if (!value) return 'Email is required'
    const normalized = normalizeEmail(value)
    if (isValidEmail(normalized)) return ''
    if (/\s/.test(normalized)) {
        return 'This looks like a name, not an email address'
    }
    if (!normalized.includes('@')) return 'An email address needs an @'
    return 'This is not a valid email address'
}
